import React, { useMemo } from "react";
import {
  Box,
  Card,
  Grid,
  Skeleton,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
} from "@material-ui/core";
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  CalendarToday,
  Timeline,
} from "@material-ui/icons";
import { fNumber } from "src/utils/formatNumber";
import Chart from "react-apexcharts";
import useChart from "../charts/use-chart";
import dayjs from "dayjs";

export default function ActividadSemanalEmpleado({ data, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Procesar datos de actividad diaria
  const actividadProcessed = useMemo(() => {
    if (!data?.actividadDiaria?.length) return { categories: [], series: [] };

    // Ordenar por fecha
    const sortedData = [...data.actividadDiaria].sort(
      (a, b) => new Date(a.fecha) - new Date(b.fecha),
    );

    return {
      categories: sortedData.map((item) => dayjs(item.fecha).format("DD/MM")),
      completadas: sortedData.map((item) => item.tareasCompletadas),
      iniciadas: sortedData.map((item) => item.tareasIniciadas),
    };
  }, [data?.actividadDiaria]);

  // Configuración del gráfico de actividad
  const chartOptions = useChart({
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: [3, 2],
      dashArray: [0, 5],
    },
    colors: ["#4caf50", "#2196f3"],
    xaxis: {
      categories: actividadProcessed.categories,
      labels: {
        style: {
          fontSize: isMobile ? "9px" : "11px",
        },
        rotate: -45,
        rotateAlways: actividadProcessed.categories.length > 15,
      },
      tickAmount: isMobile ? 7 : undefined,
    },
    yaxis: {
      labels: {
        style: {
          fontSize: isMobile ? "10px" : "12px",
        },
        formatter: (value) => Math.round(value),
      },
      min: 0,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => `${value} tareas`,
      },
    },
    grid: {
      strokeDashArray: 3,
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
      fontSize: isMobile ? "11px" : "13px",
    },
    markers: {
      size: 4,
      hover: {
        size: 6,
      },
    },
  });

  const series = [
    {
      name: "Completadas",
      data: actividadProcessed.completadas,
    },
    {
      name: "Iniciadas",
      data: actividadProcessed.iniciadas,
    },
  ];

  // Funciones de ayuda para tendencia
  const getTrendIcon = (tendencia) => {
    const iconSize = isMobile ? 20 : 24;
    if (tendencia === "creciente")
      return <TrendingUp sx={{ color: "#4caf50", fontSize: iconSize }} />;
    if (tendencia === "decreciente")
      return <TrendingDown sx={{ color: "#f44336", fontSize: iconSize }} />;
    return <TrendingFlat sx={{ color: "#ff9800", fontSize: iconSize }} />;
  };

  const getTrendColor = (tendencia) => {
    if (tendencia === "creciente") return "#4caf50";
    if (tendencia === "decreciente") return "#f44336";
    return "#ff9800";
  };

  const getTrendText = (tendencia) => {
    if (tendencia === "creciente") return "Tendencia al alza";
    if (tendencia === "decreciente") return "Tendencia a la baja";
    return "Tendencia estable";
  };

  if (loading) {
    return (
      <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Card>
    );
  }

  const {
    diaMaxProductividad,
    maxTareasCompletadas,
    promedioDiario,
    tendencia,
  } = data?.resumen || {};

  return (
    <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        gutterBottom
        fontWeight="bold"
      >
        Actividad Diaria
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        {/* Columna izquierda - Métricas */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            {/* Promedio diario */}
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                bgcolor: "primary.lighter",
                borderRadius: 2,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={1}
              >
                <Timeline
                  sx={{
                    color: "primary.main",
                    mr: 1,
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
                <Typography
                  variant={isMobile ? "body2" : "subtitle2"}
                  color="primary.main"
                  fontWeight="bold"
                >
                  Promedio Diario
                </Typography>
              </Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                color="primary.main"
                fontWeight="bold"
              >
                {fNumber(promedioDiario || 0)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                tareas completadas/día
              </Typography>
            </Box>

            {/* Día más productivo */}
            {diaMaxProductividad && (
              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "success.main",
                  borderRadius: 2,
                  bgcolor: "success.lighter",
                }}
              >
                <Stack spacing={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center">
                      <CalendarToday
                        sx={{
                          color: "success.main",
                          mr: 1,
                          fontSize: isMobile ? 18 : 20,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight="bold"
                      >
                        Mejor Día
                      </Typography>
                    </Box>
                    <Chip
                      label={fNumber(maxTareasCompletadas || 0)}
                      size="small"
                      color="success"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight="medium">
                    {dayjs(diaMaxProductividad).format("DD/MM/YYYY")}
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* Tendencia */}
            {tendencia && (
              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Stack spacing={1}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    Análisis de Tendencia
                  </Typography>
                  <Box display="flex" alignItems="center">
                    {getTrendIcon(tendencia)}
                    <Typography
                      variant="body2"
                      sx={{ color: getTrendColor(tendencia), ml: 1 }}
                      fontWeight="medium"
                    >
                      {getTrendText(tendencia)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    Últimos 7 días vs anteriores
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        </Grid>

        {/* Columna derecha - Gráfico */}
        <Grid item xs={12} lg={8}>
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              color="textSecondary"
              fontWeight="medium"
            >
              Evolución de Actividad
            </Typography>
            {actividadProcessed.categories.length > 0 ? (
              <Box sx={{ height: { xs: 300, sm: 350 } }}>
                <Chart
                  options={chartOptions}
                  series={series}
                  type="line"
                  height="100%"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.neutral",
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No hay datos de actividad en el período seleccionado
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
}
